import { useLingui } from '@lingui/react/macro';
import type { FC } from 'react';
import { useAsync } from 'react-use';
import { useParams } from 'wouter';
import { Breadcrumb } from '@/base/Breadcrumb';
import { SEO_ORIGIN } from '@/base/constants';
import { PageMeta } from '@/base/PageMeta';
import { LandingLayout } from '@/landing/LandingLayout';
import { useSettings } from '@/settings/api';
import { cn } from '@/shared/react';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { Doc } from './Doc';
import { pages } from './pages';

export const DocPage: FC = () => {
  const { page } = useParams<{ page: string }>();
  const [{ language }] = useSettings();
  const {
    i18n: { locale },
    t,
  } = useLingui();

  const content = useAsync(async () => {
    const langs = pages[page];
    // Fall back to en while the translation is not pulled yet.
    const importFn = langs?.[locale] ?? langs?.en;
    return importFn ? await importFn() : undefined;
  }, [page, locale]);

  usePupeteerSignal(!!content.value);

  return (
    <LandingLayout>
      <PageMeta
        title={content.value?.attributes?.title}
        description={content.value?.attributes?.description}
        localizedPath={`/${page}`}
      />
      <Breadcrumb
        className="w-full max-w-desktop mx-auto px-3"
        items={[
          { name: t`Home`, url: `${SEO_ORIGIN}/${language}` },
          {
            name: content.value?.attributes?.title ?? page,
            url: `${SEO_ORIGIN}/${language}/${page}`,
          },
        ]}
      />
      <main className="w-full max-w-desktop mx-auto px-3 py-8">
        <Doc
          raw={content.value?.html ?? ''}
          className={cn(!content.value && 'shimmer', 'min-h-96')}
        />
      </main>
    </LandingLayout>
  );
};
